import { useState, useRef, useEffect } from "react";

export default function AudioPlayer({ accent, label, duration: durationLabel, bg, audioSrc }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const [hasError, setHasError] = useState(false);

  const useFake = !audioSrc || hasError;

  // Fake progress for when no audio file is available
  useEffect(() => {
    if (!playing || !useFake) return;
    const iv = setInterval(() => {
      setProgress((p) => {
        if (p >= 100) {
          setPlaying(false);
          return 0;
        }
        return p + 0.5;
      });
    }, 50);
    return () => clearInterval(iv);
  }, [playing, useFake]);

  const handlePlayPause = () => {
    if (!useFake && audioRef.current) {
      if (playing) {
        audioRef.current.pause();
        setPlaying(false);
      } else {
        if (progress >= 100) setProgress(0);
        audioRef.current.play().catch(() => setHasError(true));
        setPlaying(true);
      }
    } else {
      // Fallback: fake playback
      if (playing) {
        setPlaying(false);
      } else {
        if (progress >= 100) setProgress(0);
        setPlaying(true);
      }
    }
  };

  const handleTimeUpdate = () => {
    const a = audioRef.current;
    if (a && a.duration) {
      setProgress((a.currentTime / a.duration) * 100);
      setAudioDuration(a.duration);
    }
  };

  const handleEnded = () => {
    setPlaying(false);
    setProgress(0);
  };

  const formatTime = (s) => {
    if (!s) return "0:00";
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  const bars = Array.from({ length: 48 }, (_, i) => {
    const s = Math.sin(i * 12.9898 + 78.233) * 43758.5453;
    return 0.2 + (s - Math.floor(s)) * 0.8;
  });

  return (
    <div
      style={{
        background: bg || "rgba(255,255,255,0.03)",
        border: `1px solid ${accent}25`,
        borderRadius: 14,
        padding: "12px 18px",
        marginBottom: 14,
        display: "flex",
        alignItems: "center",
        gap: 12,
      }}
    >
      {audioSrc && (
        <audio
          ref={audioRef}
          src={audioSrc}
          onTimeUpdate={handleTimeUpdate}
          onEnded={handleEnded}
          onError={() => setHasError(true)}
          preload="metadata"
        />
      )}
      <button
        onClick={handlePlayPause}
        style={{
          width: 38,
          height: 38,
          borderRadius: "50%",
          border: "none",
          cursor: "pointer",
          background: playing ? `${accent}30` : `${accent}15`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {playing ? (
          <div style={{ display: "flex", gap: 3 }}>
            <div
              style={{
                width: 3,
                height: 12,
                background: accent,
                borderRadius: 2,
              }}
            />
            <div
              style={{
                width: 3,
                height: 12,
                background: accent,
                borderRadius: 2,
              }}
            />
          </div>
        ) : (
          <div
            style={{
              width: 0,
              height: 0,
              marginLeft: 3,
              borderTop: "7px solid transparent",
              borderBottom: "7px solid transparent",
              borderLeft: `11px solid ${accent}`,
            }}
          />
        )}
      </button>
      <div style={{ flex: 1 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: 6,
          }}
        >
          <span
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: accent,
              textTransform: "uppercase",
              letterSpacing: 1,
            }}
          >
            Live Guest Call
          </span>
          <span
            style={{
              fontSize: 10,
              color: "rgba(255,255,255,0.3)",
              fontFamily: "monospace",
            }}
          >
            {audioDuration
              ? formatTime(audioDuration)
              : durationLabel || ""}
          </span>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            height: 20,
          }}
        >
          {bars.map((h, i) => (
            <div
              key={i}
              style={{
                flex: 1,
                height: `${h * 100}%`,
                minHeight: 3,
                borderRadius: 1.5,
                background:
                  (i / bars.length) * 100 < progress
                    ? accent
                    : "rgba(255,255,255,0.1)",
                transition: "background 0.1s",
              }}
            />
          ))}
        </div>
        <div
          style={{
            fontSize: 10.5,
            color: "rgba(255,255,255,0.35)",
            marginTop: 4,
          }}
        >
          {label}
        </div>
      </div>
    </div>
  );
}
